import React, { useState, Fragment } from 'react';
import { ChevronUp, ChevronDown, X, ArrowRight, MousePointerClick } from 'lucide-react';
import { FunnelStageData } from '../types';
interface ThroughputFunnelProps {
  onStageClick: (stage: FunnelStageData) => void;
  stages: FunnelStageData[];
}
function interpolateColor(t: number): string {
  // Charcoal (#3d3d3d) → Steel Blue (#5a7fa5) → Gold (#c9a84c)
  const stops = [{
    pos: 0,
    r: 61,
    g: 61,
    b: 61
  }, {
    pos: 0.35,
    r: 75,
    g: 100,
    b: 130
  }, {
    pos: 0.6,
    r: 90,
    g: 127,
    b: 165
  }, {
    pos: 0.85,
    r: 170,
    g: 155,
    b: 90
  }, {
    pos: 1,
    r: 201,
    g: 168,
    b: 76
  }];
  let lower = stops[0],
    upper = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (t >= stops[i].pos && t <= stops[i + 1].pos) {
      lower = stops[i];
      upper = stops[i + 1];
      break;
    }
  }
  const range = upper.pos - lower.pos;
  const local = range === 0 ? 0 : (t - lower.pos) / range;
  const r = Math.round(lower.r + (upper.r - lower.r) * local);
  const g = Math.round(lower.g + (upper.g - lower.g) * local);
  const b = Math.round(lower.b + (upper.b - lower.b) * local);
  return `rgb(${r}, ${g}, ${b})`;
}
export function ThroughputFunnel({
  onStageClick,
  stages
}: ThroughputFunnelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const numStages = stages.length;
  const svgWidth = 900;
  const svgHeight = 460;
  const topPadding = 10;
  const bottomPadding = 20;
  const funnelHeight = svgHeight - topPadding - bottomPadding;
  const topWidthPct = 0.92;
  const bottomWidthPct = 0.22;
  const rowHeight = funnelHeight / numStages;
  function getEdgesAtY(y: number): {
    left: number;
    right: number;
  } {
    const t = Math.max(0, Math.min(1, (y - topPadding) / funnelHeight));
    const widthPct = topWidthPct + (bottomWidthPct - topWidthPct) * t;
    const halfW = widthPct * svgWidth / 2;
    const cx = svgWidth / 2;
    return {
      left: cx - halfW,
      right: cx + halfW
    };
  }
  const toggleSelect = (index: number) => {
    setSelectedIndices(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index].sort((a, b) => a - b));
  };
  const clearSelection = () => setSelectedIndices([]);
  const sortedSelected = [...selectedIndices].sort((a, b) => a - b);
  const comparisonPairs: {
    from: FunnelStageData;
    to: FunnelStageData;
    pct: number;
  }[] = [];
  for (let i = 0; i < sortedSelected.length - 1; i++) {
    const from = stages[sortedSelected[i]];
    const to = stages[sortedSelected[i + 1]];
    const pct = from.count > 0 ? to.count / from.count * 100 : 0;
    comparisonPairs.push({
      from,
      to,
      pct
    });
  }
  const overallComparison = sortedSelected.length >= 2 ? {
    from: stages[sortedSelected[0]],
    to: stages[sortedSelected[sortedSelected.length - 1]],
    pct: stages[sortedSelected[0]].count > 0 ? stages[sortedSelected[sortedSelected.length - 1]].count / stages[sortedSelected[0]].count * 100 : 0
  } : null;
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8 overflow-hidden" data-id="element-357">
      <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50" data-id="element-358">
        <h2 className="text-lg font-bold text-[#3d3d3d] flex items-center gap-2" data-id="element-359">
          <div className="w-2 h-6 bg-[#c9a84c] rounded-sm" data-id="element-360"></div>
          Throughput Funnel
        </h2>
        <div className="flex items-center gap-2" data-id="element-361">
          {selectedIndices.length > 0 && <button onClick={clearSelection} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-[#c9a84c] transition-colors bg-white px-2.5 py-1.5 rounded-md border border-gray-200 shadow-sm hover:shadow hover:border-[#c9a84c]/30" data-id="element-362">
              <X size={12} data-id="element-363" /> Clear ({selectedIndices.length})
            </button>}
          <button onClick={() => setIsCollapsed(!isCollapsed)} className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#3d3d3d] transition-colors bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm hover:shadow" data-id="element-364">
            {isCollapsed ? 'Expand' : 'Collapse'}
            {isCollapsed ? <ChevronDown size={16} data-id="element-365" /> : <ChevronUp size={16} data-id="element-366" />}
          </button>
        </div>
      </div>

      {!isCollapsed && <>
          <div className="px-6 pt-4 pb-0 flex items-center gap-1.5 text-[11px] text-gray-400 font-medium" data-id="element-367">
            <MousePointerClick size={12} data-id="element-368" />
            Click stages to compare · Double-click to drill down
          </div>

          <div className="p-6 md:p-8 pt-3 bg-gray-50/30" data-id="element-369">
            <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} className="w-full max-w-4xl mx-auto block" preserveAspectRatio="xMidYMid meet" data-id="element-370">
              {stages.map((stage, i) => {
            const yTop = topPadding + i * rowHeight;
            const yBottom = topPadding + (i + 1) * rowHeight;
            const top = getEdgesAtY(yTop);
            const bottom = getEdgesAtY(yBottom);
            const color = interpolateColor(i / (numStages - 1));
            const isHovered = hoveredIndex === i;
            const isSelected = selectedIndices.includes(i);
            const points = `${top.left},${yTop} ${top.right},${yTop} ${bottom.right},${yBottom} ${bottom.left},${yBottom}`;
            const cx = svgWidth / 2;
            const cy = (yTop + yBottom) / 2;
            const midEdges = getEdgesAtY(cy);
            const bandWidth = midEdges.right - midEdges.left;
            const nameFontSize = bandWidth > 500 ? 14 : bandWidth > 350 ? 13 : 12;
            const countFontSize = bandWidth > 500 ? 22 : bandWidth > 350 ? 19 : 16;
            const badgeFontSize = 11;
            const dropoff = i > 0 ? stage.dropoff : null;
            const gap = 10;
            return <g key={stage.id} onClick={() => toggleSelect(i)} onDoubleClick={e => {
              e.stopPropagation();
              onStageClick(stage);
            }} onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} className="cursor-pointer" data-id="element-371">
                    {isSelected && <polygon points={points} fill="none" stroke="white" strokeWidth="4" style={{
                filter: 'drop-shadow(0 0 6px rgba(255,255,255,0.8))'
              }} data-id="element-372" />}

                    <polygon points={points} fill={color} opacity={isSelected ? 1 : isHovered ? 0.95 : 0.85} stroke={isSelected ? 'white' : 'rgba(255,255,255,0.6)'} strokeWidth={isSelected ? '2.5' : '1.5'} style={{
                filter: isHovered ? 'brightness(1.12)' : isSelected ? 'brightness(1.08)' : 'none',
                transition: 'filter 0.15s, opacity 0.15s'
              }} data-id="element-373" />

                    {isSelected && <>
                        <circle cx={top.left + 20} cy={cy} r="11" fill="white" opacity="0.95" data-id="element-374" />
                        <text x={top.left + 20} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fill={color} fontSize="11" fontWeight="800" fontFamily="Inter, sans-serif" data-id="element-375">
                          {sortedSelected.indexOf(i) + 1}
                        </text>
                      </>}

                    <clipPath id={`clip-${stage.id}`} data-id="element-376">
                      <polygon points={points} data-id="element-377" />
                    </clipPath>

                    <g clipPath={`url(#clip-${stage.id})`} data-id="element-378">
                      <text x={cx - gap} y={cy + 1} textAnchor="end" dominantBaseline="middle" fill="white" fontSize={nameFontSize} fontWeight="600" fontFamily="Inter, sans-serif" style={{
                  textShadow: '0 1px 2px rgba(0,0,0,0.25)'
                }} data-id="element-379">
                        {stage.name}
                      </text>
                      <text x={cx + gap} y={cy + 1} textAnchor="start" dominantBaseline="middle" fill="white" fontSize={countFontSize} fontWeight="800" fontFamily="Inter, sans-serif" style={{
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }} data-id="element-380">
                        {stage.count.toLocaleString()}
                      </text>
                      {i > 0 && <text x={cx + gap + String(stage.count.toLocaleString()).length * (countFontSize * 0.6) + 12} y={cy + 1} textAnchor="start" dominantBaseline="middle" fill="rgba(255,255,255,0.7)" fontSize={badgeFontSize} fontWeight="700" fontFamily="Inter, sans-serif" data-id="element-381">
                          ({stage.percentage}%)
                        </text>}
                    </g>

                    {dropoff && <text x={Math.min(top.right + 16, svgWidth - 40)} y={yTop + 6} textAnchor="start" dominantBaseline="middle" fill="#9ca3af" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif" data-id="element-382">
                        ↓ {dropoff}%
                      </text>}
                  </g>;
          })}
            </svg>
          </div>

          {sortedSelected.length >= 2 && <div className="px-6 pb-6" data-id="element-383">
              <div className="bg-[#3d3d3d] rounded-xl p-5 text-white" data-id="element-384">
                <div className="flex items-center justify-between mb-4" data-id="element-385">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-white/80" data-id="element-386">
                    Stage Comparison
                  </h3>
                  <button onClick={clearSelection} className="text-xs text-white/50 hover:text-white transition-colors" data-id="element-387">
                    Clear
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-388">
                  {sortedSelected.map((idx, pos) => {
              const stage = stages[idx];
              const color = interpolateColor(idx / (numStages - 1));
              const nextPair = comparisonPairs[pos];
              return <Fragment key={idx} data-id="element-389">
                        <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2" data-id="element-390">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{
                    backgroundColor: color
                  }} data-id="element-391"></div>
                          <div data-id="element-392">
                            <div className="text-[11px] text-white/60 font-medium" data-id="element-393">
                              {stage.name}
                            </div>
                            <div className="text-lg font-bold" data-id="element-394">
                              {stage.count.toLocaleString()}
                            </div>
                          </div>
                        </div>
                        {nextPair && <div className="flex flex-col items-center" data-id="element-395">
                            <ArrowRight size={16} className="text-white/40" data-id="element-396" />
                            <span className="text-xs font-bold text-[#c9a84c] bg-[#c9a84c]/20 px-2 py-0.5 rounded-full mt-0.5" data-id="element-397">
                              {nextPair.pct.toFixed(1)}%
                            </span>
                          </div>}
                      </Fragment>;
            })}
                </div>
                {overallComparison && sortedSelected.length > 2 && <div className="border-t border-white/10 pt-3 flex items-center gap-3" data-id="element-398">
                    <span className="text-xs text-white/50 font-medium uppercase tracking-wider" data-id="element-399">
                      Overall:
                    </span>
                    <span className="text-sm font-semibold text-white/80" data-id="element-400">
                      {overallComparison.from.name} →{' '}
                      {overallComparison.to.name}
                    </span>
                    <span className="text-sm font-bold text-[#c9a84c] bg-[#c9a84c]/20 px-2.5 py-0.5 rounded-full" data-id="element-401">
                      {overallComparison.pct.toFixed(2)}%
                    </span>
                    <span className="text-xs text-white/40" data-id="element-402">
                      ({overallComparison.to.count.toLocaleString()} of{' '}
                      {overallComparison.from.count.toLocaleString()})
                    </span>
                  </div>}
              </div>
            </div>}
        </>}
    </div>;
}